import React, { useState } from 'react';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { validatePhoneNumber } from '../../utils/validators';

interface PersonalInfoProps {
  profile: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
  };
  onUpdate: (field: string, value: string) => Promise<void>;
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({ profile, onUpdate }) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEdit = (field: string) => {
    setEditingField(field);
    setEditValue(profile[field as keyof typeof profile] || '');
    setError(null);
  };

  const handleSave = async () => {
    if (!editingField) return;

    if (editingField === 'phone') {
      const validationResult = validatePhoneNumber(editValue);
      if (!validationResult.isValid) {
        setError(validationResult.error);
        return;
      }
    }

    try {
      await onUpdate(editingField, editValue);
      setEditingField(null);
      setError(null);
    } catch (err) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const renderField = (label: string, field: string) => (
    <div className="py-4 border-b border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500">{label}</p>
          {editingField === field ? (
            <div className="mt-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="input w-full"
                autoFocus
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>
          ) : (
            <p className="text-gray-900">
              {profile[field as keyof typeof profile] || '-'}
            </p>
          )}
        </div>
        <div className="ml-4 flex items-center space-x-2">
          {editingField === field ? (
            <>
              <button
                onClick={handleSave}
                className="p-1 text-green-600 hover:bg-green-50 rounded-md"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setEditingField(null);
                  setError(null);
                }}
                className="p-1 text-gray-600 hover:bg-gray-50 rounded-md"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => handleEdit(field)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-1">
      {renderField('Prénom', 'firstName')}
      {renderField('Nom', 'lastName')}
      {renderField('Téléphone', 'phone')}
      {renderField('Adresse', 'address')}
    </div>
  );
};

export default PersonalInfo;